/*
  EcoBerry mega menu for Dawn 15 (replaces the Buddha Mega Menu app).

  Each top-level item is Dawn's <header-menu><details>, so Dawn keeps handling Escape,
  focus-out and the sticky header. This element adds:
  - open on hover with a short delay (mouse and trackpad only, touch keeps tap-to-open)
  - a second click on an open item goes to its collection, like Buddha
  - close on outside click, on Escape and after Add to cart
  - theme editor: selecting a "Mega menu item" or "Menu image" block opens its submenu
*/
if (!customElements.get('eb-mega-menu')) {
  customElements.define(
    'eb-mega-menu',
    class EbMegaMenu extends HTMLElement {
      constructor() {
        super();
        this.openDelay = 90;
        this.switchDelay = 160;
        this.closeDelay = 280;
        this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.onBlockDeselect = this.onBlockDeselect.bind(this);
      }

      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.eb-mm__item--parent'));

        if (!this.bound) {
          this.items.forEach((item) => this.bindItem(item));
          this.bound = true;
        }

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.closeAll());
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        if (this.unsubscribeCartUpdate) this.unsubscribeCartUpdate();
        (this.items || []).forEach((item) => this.clearTimers(item));
      }

      bindItem(item) {
        const details = item.querySelector('details');
        const summary = details && details.querySelector('summary');
        if (!summary) return;

        item.addEventListener('mouseenter', () => this.onPointerEnter(item, details));
        item.addEventListener('mouseleave', () => this.onPointerLeave(item, details));
        summary.addEventListener('click', (event) => this.onSummaryClick(event, item, details, summary));
        details.addEventListener('toggle', () => this.onToggle(details, summary));
      }

      get hoverEnabled() {
        return this.dataset.trigger !== 'click' && this.hoverQuery.matches;
      }

      onPointerEnter(item, details) {
        if (!this.hoverEnabled) return;
        this.clearTimers(item);
        const switching = this.items.some((other) => other !== item && other.querySelector('details[open]'));
        item.ebOpenTimer = setTimeout(
          () => {
            details.dataset.ebHover = 'true';
            this.open(details);
          },
          // A short pause before switching stops a diagonal pointer path from hijacking the open panel.
          switching ? this.switchDelay : this.openDelay
        );
      }

      onPointerLeave(item, details) {
        if (!this.hoverEnabled) return;
        this.clearTimers(item);
        if (!details.open || details.dataset.ebHover !== 'true') return;
        item.ebCloseTimer = setTimeout(() => this.close(details), this.closeDelay);
      }

      onSummaryClick(event, item, details, summary) {
        // Keyboard activation (detail 0) and click mode keep Dawn's native <details> toggle.
        if (!this.hoverEnabled || event.detail === 0) {
          details.dataset.ebHover = 'false';
          return;
        }

        event.preventDefault();
        this.clearTimers(item);

        if (!details.open) {
          details.dataset.ebHover = 'true';
          this.open(details);
        } else if (item.dataset.href) {
          window.location.href = item.dataset.href;
        } else {
          this.close(details);
        }

        // Dawn's global summary handler guesses aria-expanded before the toggle; set the real value.
        setTimeout(() => summary.setAttribute('aria-expanded', details.open));
      }

      onToggle(details, summary) {
        summary.setAttribute('aria-expanded', details.open);

        if (!details.open) {
          delete details.dataset.ebHover;
          return;
        }

        this.items.forEach((item) => {
          const other = item.querySelector('details');
          if (other && other !== details && other.open) this.close(other);
        });
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) this.closeAll();
      }

      onDocumentKeydown(event) {
        if (event.key !== 'Escape') return;
        const openDetails = this.querySelector('details[open]');
        // When focus is inside the menu, Dawn's own Escape handler closes it and restores focus.
        if (!openDetails || openDetails.contains(document.activeElement)) return;
        this.closeAll();
      }

      onBlockSelect(event) {
        const details = event.target.closest && event.target.closest('details');
        if (!details || !this.contains(details)) return;
        details.dataset.ebHover = 'false';
        this.open(details);
      }

      onBlockDeselect(event) {
        const details = event.target.closest && event.target.closest('details');
        if (!details || !this.contains(details)) return;
        this.close(details);
      }

      open(details) {
        if (!details.open) details.setAttribute('open', '');
      }

      close(details) {
        const headerMenu = details.closest('header-menu');
        if (headerMenu && typeof headerMenu.close === 'function') {
          headerMenu.close();
        } else {
          details.removeAttribute('open');
        }
        delete details.dataset.ebHover;
      }

      closeAll() {
        (this.items || []).forEach((item) => {
          this.clearTimers(item);
          const details = item.querySelector('details');
          if (details && details.open) this.close(details);
        });
      }

      clearTimers(item) {
        clearTimeout(item.ebOpenTimer);
        clearTimeout(item.ebCloseTimer);
      }
    }
  );
}